import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import useFormSteps from '../../hooks/useFormSteps';
import getFieldsFromForm from '../../lib/getFieldsFromForm';
import getVisibleFieldsFromForm from '../../lib/getVisibleFieldsFromForm';
import isInstantAnswerField from '../../lib/isInstantAnswerField';

import Blocks from '../blocks/Blocks';
import FormButton from '../buttons/FormButton';
import CheckIcon from '../icons/CheckIcon';
import FormAnswers from '../partials/FormAnswers';
import FormStepsModal from '../popups/FormStepsModal';
import FormDescription from '../typography/FormDescription';
import FormQuestion from './FormQuestion';

import styles from '../../styles/forms/form-steps.module.css';

type Answers = Record<string, unknown>;

interface FormStepsProps {
    form: Form;
    className?: string | null;
}

/**
 * A form answered like a survey, one question at a time: the first question stays in the block
 * and the next ones open in a modal, so the block keeps its size in the page
 */
function FormSteps({ form, className = null }: FormStepsProps) {
    const { description = null, results = null } = form as Form & {
        description?: string | null;
        results?: { blocks?: Block[] } | null;
    };
    const { blocks: resultsBlocks = null } = results || {};
    const { answers, fields, errors, started, completed, answer, complete } = useFormSteps(form);
    const [firstField = null] = fields;
    const hasManyQuestions = getFieldsFromForm(form).length > 1;
    const [value, setValue] = useState<unknown>(null);
    const [loading, setLoading] = useState(false);
    const [modalField, setModalField] = useState<string | null>(null);
    const firstValue = value ?? (firstField !== null ? (answers[firstField.name] ?? null) : null);

    // Opens the next questions, or completes a form that has no other question for this answer
    const continueFrom = useCallback(
        (newAnswers: Answers) => {
            const newFields = getVisibleFieldsFromForm(form, newAnswers);
            if (newFields.length > 1) {
                const nextField =
                    newFields.find(
                        ({ name }, index) => index > 0 && typeof newAnswers[name] === 'undefined',
                    ) || newFields[1];
                setModalField(nextField.name);
                return;
            }
            setLoading(true);
            complete(newAnswers).then(() => setLoading(false));
        },
        [form, complete],
    );

    const answerFirst = useCallback(
        (newValue: unknown) => {
            if (newValue === null || newValue === '') {
                return;
            }
            if (newValue === answers[firstField.name]) {
                continueFrom(answers);
                return;
            }
            setLoading(true);
            answer(firstField.name, newValue).then((newAnswers) => {
                setLoading(false);
                if (newAnswers !== null) {
                    continueFrom(newAnswers);
                }
            });
        },
        [answers, firstField, answer, continueFrom],
    );

    const onChange = useCallback(
        (newValue: unknown) => {
            setValue(newValue);
            if (isInstantAnswerField(firstField)) {
                answerFirst(newValue);
            }
        },
        [firstField, answerFirst],
    );

    const onModalClosed = useCallback(() => {
        setModalField(null);
        setValue(null);
    }, []);

    if (firstField === null) {
        return null;
    }

    const instant = isInstantAnswerField(firstField);

    return (
        <div className={classNames([styles.container, className])}>
            {/* The first question keeps the block's height once the form is completed */}
            <div className={classNames([styles.content, { [styles.hidden]: completed }])}>
                {description !== null ? (
                    <FormDescription tag="div" html={description} className={styles.description} />
                ) : null}
                <FormQuestion
                    field={firstField}
                    value={firstValue}
                    errors={errors !== null ? errors[firstField.name] || null : null}
                    loading={loading}
                    labelClassName={styles.question}
                    descriptionClassName={styles.questionDescription}
                    fieldClassName={styles.field}
                    onChange={onChange}
                />
                <div className={styles.footer}>
                    {started && hasManyQuestions ? (
                        <FormButton
                            className={styles.button}
                            loading={loading}
                            onClick={() => answerFirst(firstValue)}
                        >
                            <FormattedMessage
                                defaultMessage="Continuer le sondage"
                                description="Button label"
                            />
                        </FormButton>
                    ) : null}
                    {!(started && hasManyQuestions) && !instant ? (
                        <FormButton
                            className={styles.button}
                            loading={loading}
                            onClick={() => answerFirst(firstValue)}
                        >
                            <FormattedMessage
                                defaultMessage="Continuer"
                                description="Button label"
                            />
                        </FormButton>
                    ) : null}
                    {!started && instant && hasManyQuestions ? (
                        <p className={styles.hint}>
                            <FormattedMessage
                                defaultMessage="Ta réponse ouvre la suite du sondage"
                                description="Hint under the first question of a survey"
                            />
                        </p>
                    ) : null}
                </div>
            </div>

            {completed ? (
                <div className={styles.done}>
                    {/* The results of the form already thank the reader */}
                    {resultsBlocks !== null ? (
                        <Blocks blocks={resultsBlocks} withoutMaxWidth className={styles.results} />
                    ) : (
                        <div className={styles.doneHeader}>
                            <CheckIcon
                                circleColor="var(--color-brand-default)"
                                className={styles.check}
                            />
                            <div className={styles.doneTitle}>
                                <FormattedMessage
                                    defaultMessage="Merci d’avoir répondu au sondage."
                                    description="Thank you message of a survey"
                                />
                            </div>
                        </div>
                    )}
                    <FormAnswers fields={fields} answers={answers} />
                </div>
            ) : null}

            {modalField !== null ? (
                <FormStepsModal
                    form={form}
                    answers={answers}
                    completed={completed}
                    initialField={modalField}
                    errors={errors}
                    onAnswer={answer}
                    onComplete={complete}
                    onClosed={onModalClosed}
                />
            ) : null}
        </div>
    );
}

export default FormSteps;
